import Footer from '@bridebook/web/app-basic/components/footer/footer';
import Head from '@bridebook/web/app-basic/components/head/head';
import { TopBar } from '@bridebook/web/app-basic/components/header';
import { ReviewSupplierModal } from '@bridebook/web/app-basic/components/review-supplier-modal';
import { SupplierSuggestions } from '@bridebook/web/app-basic/components/supplier-suggestions';
import { setAlternateLinks } from '@bridebook/web/app-basic/lib/app/app-slice';
import { getIsMobile } from '@bridebook/web/app-basic/lib/app/selectors';
import {
  EdgeCacheTTL,
  basicGSSPWrapper,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import uuid from 'uuid-random';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { Snackbar } from '@bridebook/ui';
import { TSnackbarMode } from '@bridebook/ui/src/components/bbcommon/snackbar/snackbar-content';
import { IReviewSupplierFormProps } from '@bridebook/web/app-shared/components/review-supplier-form';
import { Reviews } from '@bridebook/web/app-shared/components/reviews/reviews';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { useFetchSupplierByPublicId } from '@bridebook/web/app-shared/lib/search/hooks/use-fetch-supplier';
import { AppType } from '@bridebook/web/pages/_app';
import { getAlternateLinksForRegularPage } from '@bridebook/web/lib/utils/i18n-routing/get-alternate-links';
import { getLocalisedCanonical } from '@bridebook/web/lib/utils/i18n-routing/get-localised-canonical';

const SNACKBAR_TIMEOUT = 3000;
interface SnackbarProps {
  show: boolean;
  mode: TSnackbarMode;
  text: string;
}

const defaultSnackbarProps: SnackbarProps = {
  show: false,
  mode: 'success',
  text: '',
};

const ReviewsPage = () => {
  const { query } = useRouter();
  const { t } = useTranslation();
  const market = useMarket();
  const isMobile = useSelector(getIsMobile);
  const [searchTerm, setSearchTerm] = useState('');
  const [supplier, setSupplier] = useState<ISupplier | null>(null);
  const [snackbarProps, setSnackbarProps] = useState<SnackbarProps>(defaultSnackbarProps);
  const canonicalUrl = useMemo(() => getLocalisedCanonical(market, 'reviews'), [market]);

  // Fetch supplier by public id and set returned data in state
  useFetchSupplierByPublicId(query?.publicId as string, market.locale, setSupplier);

  // Closes the review form modal
  const onCloseForm: IReviewSupplierFormProps['onCloseForm'] = useCallback(() => {
    setSupplier(null);
    setSearchTerm('');
  }, []);

  // Save review form handler
  const onSaveReview = useCallback(() => {
    setSupplier(null);
    setSearchTerm('');
    setSnackbarProps({ show: true, mode: 'success', text: t('supplier:snackbar.reviewAdded') });

    setTimeout(() => {
      setSnackbarProps(defaultSnackbarProps);
    }, SNACKBAR_TIMEOUT);
  }, [t]);

  const onError = useCallback(() => {
    setSupplier(null);
    setSearchTerm('');
    setSnackbarProps({ show: true, mode: 'alert', text: t('supplier:snackbar.reviewFailed') });

    setTimeout(() => {
      setSnackbarProps(defaultSnackbarProps);
    }, SNACKBAR_TIMEOUT);
  }, [t]);

  return (
    <>
      <Head ogUrl={canonicalUrl} title={t('supplier:review.page.title')} />
      <TopBar />
      <Reviews
        isMobile={isMobile}
        suggestions={
          <SupplierSuggestions
            onSuggestionSelect={setSupplier}
            onSuggestionsFetchRequest={setSearchTerm}
            searchTerm={searchTerm}
          />
        }
        reviewModal={
          supplier?.id && (
            <ReviewSupplierModal
              supplier={supplier}
              onSaveReview={onSaveReview}
              onCloseForm={onCloseForm}
              onError={onError}
            />
          )
        }
      />
      <Footer />
      <Snackbar
        uuid={uuid()}
        show={snackbarProps.show}
        mode={snackbarProps.mode}
        text={snackbarProps.text}
      />
    </>
  );
};

export const getServerSideProps = basicGSSPWrapper(
  ['supplier', 'search'],
  async ({ initialAppState, store }) => {
    const { pathname } = initialAppState;

    const alternate = getAlternateLinksForRegularPage(pathname);
    store.dispatch(setAlternateLinks(alternate));

    return {
      props: {},
    };
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.HOUR,
      sMaxAge: EdgeCacheTTL.HOUR,
      staleWhileRevalidate: EdgeCacheTTL.WEEK,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.TEN_MINUTES,
      sMaxAge: EdgeCacheTTL.TEN_MINUTES,
      staleWhileRevalidate: EdgeCacheTTL.HOUR,
    },
  },
);

ReviewsPage.AppType = AppType.Basic;

export default ReviewsPage;
